import React, { useState, useEffect, useMemo } from 'react';
import { I, BrandMark, TopBar, fmt, ErrorsDiamond } from './shared';
import { BattingTab, PitchingTab, FieldingTab, GameLogTab } from './tabs';
import { api } from './api';

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = e => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

const TABS = [
  { k: 'team', label: 'Team',     Icon: I.Home  },
  { k: 'bat',  label: 'Batting',  Icon: I.Bat   },
  { k: 'pit',  label: 'Pitching', Icon: I.Mound },
  { k: 'fld',  label: 'Fielding', Icon: I.Glove },
  { k: 'log',  label: 'Game Log', Icon: I.List  },
];

export default function StatsView({ team, onBack, onLogout, onOpenGame, onOpenPlayer, onStatsLoaded, theme, onToggleTheme }) {
  const [tab, setTab] = useState('team');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setStats(null); setError(null);
    api.getSeasonStats(team.teamId || team.id, team.games)
      .then(data => { setStats(data); if (onStatsLoaded) onStatsLoaded(data); })
      .catch(err => setError(err.message));
  }, [team.teamId || team.id]); // eslint-disable-line

  // Bundle stats into every onOpenGame call so BoxScoreDrawer always has them
  function handleOpenGame(game) {
    onOpenGame({ ...game, _stats: stats });
  }

  const subtitle = stats ? `${stats.gamesProcessed} games · ${stats.summary.wins}-${stats.summary.losses}` : '';

  return (
    <div className="page stats-page">
      <TopBar
        left={<>
          <button className="icon-btn" onClick={onBack} aria-label="Back"><I.Back/></button>
          <span className="crumb">
            <BrandMark/>
            <span className="crumb-sep">/</span>
            <span className="crumb-current">{team.name}</span>
            {subtitle && <span style={{color:'var(--text-3)',fontSize:12}}> · {subtitle}</span>}
          </span>
        </>}
        right={<button className="btn btn-ghost" onClick={onLogout}><I.Logout/> Sign out</button>}
        theme={theme} onToggleTheme={onToggleTheme}
      />

      <div className="tab-bar-top">
        {TABS.map(t => <button key={t.k} className={`tab-top ${tab===t.k?'active':''}`} onClick={()=>setTab(t.k)}><t.Icon size={16}/> {t.label}</button>)}
      </div>

      <main className="page-main">
        <div className="shell">
          {!stats && !error && <StatsLoadingSkeleton games={team.gameCount||17}/>}
          {error && (
            <div className="empty">
              <h3>Couldn't load stats</h3>
              <p>{error}</p>
              <p style={{marginTop:8,fontSize:13,color:'var(--text-3)'}}>
                A <a href="https://gc.com/pricing" target="_blank" rel="noreferrer" style={{color:'var(--text-2)',textDecoration:'underline'}}>GameChanger Plus subscription</a> is required to access stats and box scores.
              </p>
              <button className="btn btn-secondary" onClick={onBack}>← Pick another team</button>
            </div>
          )}
          {stats && <>
            {tab==='team' && <TeamTab stats={stats} team={team} onOpenPlayer={onOpenPlayer} onOpenGame={handleOpenGame}/>}
            {tab==='bat'  && <BattingTab  stats={stats} onOpenPlayer={onOpenPlayer}/>}
            {tab==='pit'  && <PitchingTab stats={stats} onOpenPlayer={onOpenPlayer}/>}
            {tab==='fld'  && <FieldingTab stats={stats}/>}
            {tab==='log'  && <GameLogTab  stats={stats} onOpenGame={handleOpenGame}/>}
          </>}
        </div>
      </main>

      <nav className="bottom-nav">
        {TABS.map(t => <button key={t.k} className={`bottom-tab ${tab===t.k?'active':''}`} onClick={()=>setTab(t.k)}><t.Icon/><span>{t.label}</span></button>)}
      </nav>
    </div>
  );
}

function StatsLoadingSkeleton({ games }) {
  return (
    <div>
      <div className="hero" style={{background:'var(--surface)',border:'1px solid var(--border)',color:'var(--text)'}}>
        <div className="hero-grid">
          <div className="skel" style={{height:60,width:200}}/>
          <div>
            <div className="skel" style={{height:22,width:180,marginBottom:8}}/>
            <div className="skel" style={{height:12,width:140,marginBottom:14}}/>
            <div style={{display:'flex',gap:18}}>
              <div className="skel" style={{height:32,width:60}}/><div className="skel" style={{height:32,width:60}}/><div className="skel" style={{height:32,width:60}}/>
            </div>
          </div>
          <div className="skel" style={{height:40,width:120}}/>
        </div>
      </div>
      <div className="kpi-row">
        {[1,2,3,4,5,6].map(i=><div key={i} className="kpi"><div className="skel" style={{height:11,width:70}}/><div className="skel" style={{height:26,width:80,marginTop:6}}/></div>)}
      </div>
      <div className="card">
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
          <div className="spinner"/>
          <div>
            <div style={{fontWeight:600}}>Fetching {games} games from GameChanger</div>
            <div style={{fontSize:12,color:'var(--text-3)',marginTop:2}}>This may take 30–60 seconds for a full season.</div>
          </div>
        </div>
        <div className="skel" style={{height:8,width:'60%'}}/>
      </div>
    </div>
  );
}

function topN(arr, key, n, filterFn, asc) {
  let xs = filterFn ? arr.filter(filterFn) : arr.slice();
  xs.sort((a,b) => asc ? a[key]-b[key] : b[key]-a[key]);
  return xs.slice(0,n);
}
function cap(s) { return s ? s[0].toUpperCase()+s.slice(1) : ''; }

function TeamTab({ stats, team, onOpenPlayer, onOpenGame }) {
  const s = stats.summary;
  const isMobile = useIsMobile();
  // Only count completed games (has at-bats recorded) — filters out accidentally-started games
  const completedGames = stats.teamGameLog.filter(g => (g.bat_AB || 0) > 0 || (g.bat_R || 0) > 0 || (g.pit_IP || 0) > 0);
  // Always exactly 5, with Result fallback
  const lastFive = completedGames.slice(-5).map(g => ({
    ...g,
    Result: g.Result || (g.bat_R > g.pit_R ? 'W' : g.bat_R < g.pit_R ? 'L' : 'T'),
  }));
  const pitch = useMemo(() => {
    const ipToOuts = v => { const i=Math.trunc(v||0),f=Math.round(((v||0)-i)*10); return i*3+f; };
    const outs=completedGames.reduce((a,g)=>a+ipToOuts(g.pit_IP),0);
    const ip=outs/3;
    const ER=completedGames.reduce((a,g)=>a+(g.pit_ER||0),0);
    const BB=completedGames.reduce((a,g)=>a+(g.pit_BB||0),0);
    const H =completedGames.reduce((a,g)=>a+(g.pit_H ||0),0);
    const K =completedGames.reduce((a,g)=>a+(g.pit_SO||0),0);
    return { ERA:ip>0?(ER*7/ip):0, WHIP:ip>0?((BB+H)/ip):0, KperG:completedGames.length?K/completedGames.length:0 };
  }, [completedGames]);

  return (
    <div>
      {/* Hero */}
      <div className="hero" role="region" aria-label="Season summary">
        <div className="hero-grid">
          <div>
            <div className="hero-label">Record</div>
            <div className="hero-record"><span className="w">{s.wins}</span><span className="sep">–</span><span className="l">{s.losses}</span></div>
          </div>
          <div style={{textAlign:'center'}}>
            <div className="hero-team">{team.name}</div>
            <div className="hero-meta">{cap(team.season_name)} {team.season_year} · Softball</div>
            <div style={{borderTop:'1px solid rgba(255,255,255,0.15)',marginTop:14,paddingTop:10,textAlign:'center'}}>
              <div style={{fontSize:14,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.14em',color:'rgba(255,255,255,0.7)',marginBottom:8}}>Team Statistics</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:0,width:'100%'}}>
                {[['AVG',fmt.avg(s.teamAVG)],['OBP',fmt.avg(s.teamOBP)],['SLG',fmt.avg(s.teamSLG)],['OPS',fmt.avg(s.teamOPS)],['ERA',pitch.ERA.toFixed(2)],['WHIP',pitch.WHIP.toFixed(2)],['K/G',pitch.KperG.toFixed(1)]].map(([k,v])=>(
                  <div key={k} className="hero-stat" style={{alignItems:'center',textAlign:'center'}}><div className="hero-stat-v hero-stat-v--avg">{v}</div><div className="hero-stat-k">{k}</div></div>
                ))}
              </div>
            </div>
            <div style={{borderTop:'1px solid rgba(255,255,255,0.15)',marginTop:12,paddingTop:10,textAlign:'center'}}>
              <div style={{fontSize:14,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.14em',color:'rgba(255,255,255,0.7)',marginBottom:8}}>Team Scoring</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:0,width:'100%'}}>
              <div className="hero-stat" style={{alignItems:'center',textAlign:'center'}}><div className="hero-stat-v">{s.runsScoredPerGame.toFixed(2)}</div><div className="hero-stat-k">RS/G</div></div>
              <div className="hero-stat" style={{alignItems:'center',textAlign:'center'}}><div className="hero-stat-v">{s.runsAllowedPerGame.toFixed(2)}</div><div className="hero-stat-k">RA/G</div></div>
              <div className="hero-stat" style={{alignItems:'center',textAlign:'center'}}>
                <div className="hero-stat-v" style={{color:s.runsScoredPerGame-s.runsAllowedPerGame>=0?'var(--good)':'var(--red)'}}>
                  {s.runsScoredPerGame-s.runsAllowedPerGame>=0?'+':''}{(s.runsScoredPerGame-s.runsAllowedPerGame).toFixed(2)}
                </div>
                <div className="hero-stat-k">Diff</div>
              </div>
              <div className="hero-stat" style={{alignItems:'center',textAlign:'center'}}>
                <div className="hero-stat-v">{fmt.avg(s.wins/(s.wins+s.losses))}</div>
                <div className="hero-stat-k">Win Pct</div>
              </div>
            </div>
            </div>
          </div>
          <div className="hero-form" style={{alignItems:'center'}}>
            <span className="hero-form-label" style={{fontSize:14}}>Last 5</span>
            <div className="form-strip">
              {lastFive.map((g,i)=><span key={i} className={`form-pill ${g.Result.toLowerCase()}`}>{g.Result}</span>)}
            </div>
            <div className="hero-form-mini">
              <div className="hero-form-mini-stat">
                <div className="hero-form-mini-v">{(lastFive.reduce((a,g)=>a+(g.bat_R||0),0)/Math.max(1,lastFive.length)).toFixed(2)}</div>
                <div className="hero-form-mini-k">RS/G</div>
              </div>
              <div className="hero-form-mini-stat">
                <div className="hero-form-mini-v">{(lastFive.reduce((a,g)=>a+(g.pit_R||0),0)/Math.max(1,lastFive.length)).toFixed(2)}</div>
                <div className="hero-form-mini-k">RA/G</div>
              </div>
              <div className="hero-form-mini-stat">
                {(() => {
                  const rs = lastFive.reduce((a,g)=>a+(g.bat_R||0),0)/Math.max(1,lastFive.length);
                  const ra = lastFive.reduce((a,g)=>a+(g.pit_R||0),0)/Math.max(1,lastFive.length);
                  const diff = rs - ra;
                  return <>
                    <div className="hero-form-mini-v" style={{color:diff>=0?'var(--good)':'var(--red)'}}>{diff>=0?'+':''}{diff.toFixed(2)}</div>
                    <div className="hero-form-mini-k">Diff</div>
                  </>;
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Team stats table — above calendar on mobile, first on desktop */}
      <TeamStatsTable stats={stats} onOpenPlayer={onOpenPlayer}/>

      {/* Team defense */}
      <div className="card">
        <div className="card-h">
          <h2>Team Defense</h2>
          <span className="sub">{Object.values(stats.defensiveErrors||{}).reduce((a,b)=>a+b,0)} errors · bigger circle = more errors</span>
        </div>
        <div className="diamond-wrap" style={{padding:16}}>
          <ErrorsDiamond errors={stats.defensiveErrors||{}}/>
        </div>
      </div>

      {/* Season calendar */}
      <div className="card">
        <div className="card-h">
          <h2>Season calendar</h2>
          <span className="sub">
            <span className="cal-legend"><span className="cal-swatch cal-win"/>Win</span>
            <span className="cal-legend"><span className="cal-swatch cal-loss"/>Loss</span>
          </span>
        </div>
        <SeasonCalendar games={completedGames} onOpenGame={onOpenGame}/>
      </div>
    </div>
  );
}

function SeasonCalendar({ games, onOpenGame }) {
  const withDates = useMemo(() => {
    // GC date strings like "Mar 7" lack a year, so new Date() on them is unreliable.
    // Instead, spread games evenly starting ~(games.length * 5 days) before today.
    const totalDays = games.length * 5;
    const start = new Date();
    start.setDate(start.getDate() - totalDays);
    start.setHours(0,0,0,0);
    return games.map((g, i) => ({
      ...g,
      dateObj: new Date(start.getTime() + i * 5 * 86400000),
    }));
  }, [games]);

  const months = useMemo(() => {
    const map = new Map();
    withDates.forEach(g => {
      const key = `${g.dateObj.getFullYear()}-${g.dateObj.getMonth()}`;
      if (!map.has(key)) map.set(key, {year:g.dateObj.getFullYear(),month:g.dateObj.getMonth(),games:[]});
      map.get(key).games.push(g);
    });
    return [...map.values()];
  }, [withDates]);

  return (
    <div className="cal-months">
      {months.map(m=><CalendarMonth key={`${m.year}-${m.month}`} {...m} onOpenGame={onOpenGame}/>)}
    </div>
  );
}

function CalendarMonth({ year, month, games, onOpenGame }) {
  const monthName = new Date(year,month,1).toLocaleString('en-US',{month:'long',year:'numeric'});
  const firstDay = new Date(year,month,1).getDay();
  const daysInMonth = new Date(year,month+1,0).getDate();
  const cells = [];
  for (let i=0;i<firstDay;i++) cells.push({kind:'pad',key:`p${i}`});
  for (let d=1;d<=daysInMonth;d++) {
    const game = games.find(g=>g.dateObj.getDate()===d);
    cells.push({kind:'day',day:d,game,key:`d${d}`});
  }
  while (cells.length%7) cells.push({kind:'pad',key:`t${cells.length}`});
  return (
    <div className="cal-month">
      <div className="cal-month-h">{monthName}</div>
      <div className="cal-grid">
        {['S','M','T','W','T','F','S'].map((d,i)=><div key={`dow${i}`} className="cal-dow">{d}</div>)}
        {cells.map(c => {
          if (c.kind==='pad') return <div key={c.key} className="cal-cell cal-pad" aria-hidden="true"/>;
          if (!c.game) return <div key={c.key} className="cal-cell cal-off"><span className="cal-day">{c.day}</span></div>;
          const isWin=c.game.Result==='W';
          return (
            <button key={c.key} className={`cal-cell cal-game cal-${isWin?'win':'loss'}`} onClick={()=>onOpenGame(c.game)} aria-label={`${c.game.Result} ${c.game.bat_R}-${c.game.pit_R} vs ${c.game.linkText}`}>
              <span className="cal-day">{c.day}</span>
              <span className="cal-score">{c.game.bat_R}–{c.game.pit_R}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const TEAM_COLS = [
  {key:'playerName',label:'Player',sticky:true,numeric:false,divEnd:true},
  {key:'Games',label:'G'},{key:'AB',label:'AB'},{key:'H',label:'H'},
  {key:'2B',label:'2B'},{key:'HR',label:'HR'},{key:'R',label:'R'},
  {key:'RBI',label:'RBI'},{key:'BB',label:'BB'},{key:'SO',label:'SO'},{key:'SB',label:'SB',divEnd:true},
  {key:'AVG',label:'AVG',fmt:'avg',rate:true},{key:'OBP',label:'OBP',fmt:'avg',rate:true},
  {key:'SLG',label:'SLG',fmt:'avg',rate:true},{key:'OPS',label:'OPS',fmt:'avg',rate:true,hi:true,divEnd:true},
  {key:'_IP',label:'IP',fmt:'ip',rate:true},{key:'_ERA',label:'ERA',fmt:'dec2',rate:true},
  {key:'_WHIP',label:'WHIP',fmt:'dec2',rate:true},{key:'_K',label:'K',rate:true,divEnd:true},
  {key:'_E',label:'E',rate:true},
];
const POS_KEYS=['P','C','1B','2B','3B','SS','LF','CF','RF'];
function primaryPosOf(f) {
  if (!f) return null; let best=null,max=0;
  for (const k of POS_KEYS) { const g=f['G_'+k]||0; if(g>max){max=g;best=k;} }
  return max>0?best:null;
}

function TeamStatsTable({ stats, onOpenPlayer }) {
  const [sort,setSort]=useState({key:'OPS',dir:'desc'});
  const [q,setQ]=useState('');
  const isMobile = useIsMobile();
  const rows=useMemo(()=>{
    const by=new Map();
    for (const b of stats.battingSeason) by.set(b.playerName,{...b});
    for (const p of stats.pitchingSeason) {
      const r=by.get(p.playerName)||{playerName:p.playerName,number:p.number};
      r._IP=p.IP;r._ERA=p.ERA;r._WHIP=p.WHIP;r._K=p.SO;by.set(p.playerName,r);
    }
    for (const f of stats.fieldingSeason) {
      const r=by.get(f.playerName)||{playerName:f.playerName,number:f.number};
      r._E=f.E||0; r._TC=f.TC||0; r._FPCT=f.FPCT||0; r._pos=primaryPosOf(f);
      if(!r.Games)r.Games=f.Games;by.set(f.playerName,r);
    }
    return [...by.values()];
  },[stats]);
  const filtered=q?rows.filter(r=>r.playerName.toLowerCase().includes(q.toLowerCase())):rows;
  const sorted=useMemo(()=>{
    const xs=filtered.slice();
    xs.sort((a,b)=>{
      let av=a[sort.key],bv=b[sort.key];
      if(sort.key==='playerName'){av=String(av||'');bv=String(bv||'');return sort.dir==='asc'?av.localeCompare(bv):bv.localeCompare(av);}
      av=Number.isFinite(+av)?+av:-Infinity;bv=Number.isFinite(+bv)?+bv:-Infinity;
      return sort.dir==='asc'?av-bv:bv-av;
    });
    return xs;
  },[filtered,sort]);
  function onSort(key,numeric){setSort(s=>s.key===key?{key,dir:s.dir==='desc'?'asc':'desc'}:{key,dir:numeric?'desc':'asc'});}

  if (isMobile) {
    return (
      <div className="table-card" style={{marginBottom:16}}>
        <div className="table-h">
          <h2>Team stats</h2><span className="sub">{rows.length} players · tap for details</span>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
          {sorted.map(p => (
            <button key={p.playerName} onClick={()=>onOpenPlayer(p)} style={{
              display:'flex',flexDirection:'column',gap:8,padding:'10px 12px',
              background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,
              textAlign:'left',cursor:'pointer',font:'inherit',color:'var(--text)',width:'100%',
            }}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="player-num" style={{flexShrink:0}}>{p.number||'—'}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{p.playerName.toUpperCase()}{p._pos ? <span style={{fontWeight:500,color:'var(--text-3)',fontSize:12}}> · {p._pos}</span> : null}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontFamily:'var(--font-mono)',fontSize:16,fontWeight:700,color:'var(--text)'}}>{fmt.avg(p.OPS)} <span style={{fontSize:10,fontWeight:600,color:'var(--text-3)'}}>OPS</span></div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:4,borderTop:'1px solid var(--border)',paddingTop:8}}>
                {[['AVG',fmt.avg(p.AVG)],['OBP',fmt.avg(p.OBP)],['H',p.H],['HR',p.HR],['RBI',p.RBI]].map(([k,v])=>(
                  <div key={k} style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                    <span style={{fontSize:8,fontWeight:700,textTransform:'uppercase',color:'var(--text-3)'}}>{k}</span>
                    <span style={{fontSize:12,fontWeight:600,fontFamily:'var(--font-mono)'}}>{v??'—'}</span>
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="table-card" style={{marginBottom:16}}>
      <div className="table-h">
        <h2>Team stats</h2><span className="sub">{rows.length} players · sortable · click row for details</span>
        <span className="spacer"/><input className="search" placeholder="Search player…" value={q} onChange={e=>setQ(e.target.value)}/>
      </div>
      <div className="t-scroll">
        <table className="dt">
          <thead>
            <tr>
              <th colSpan="1" className="first" style={{borderRight:'1px solid var(--border)'}}></th>
              <th colSpan="10" style={{textAlign:'center',borderRight:'1px solid var(--border)',color:'var(--text)',fontSize:10,letterSpacing:'0.08em'}}>BATTING</th>
              <th colSpan="4" style={{textAlign:'center',borderRight:'1px solid var(--border)',color:'var(--text)',fontSize:10,letterSpacing:'0.08em'}}>RATE</th>
              <th colSpan="4" style={{textAlign:'center',borderRight:'1px solid var(--border)',color:'var(--text)',fontSize:10,letterSpacing:'0.08em'}}>PITCHING</th>
              <th colSpan="1" style={{textAlign:'center',color:'var(--text)',fontSize:10,letterSpacing:'0.08em'}}>FLD</th>
            </tr>
            <tr>
              {TEAM_COLS.map(col=>(
                <th key={col.key} className={`${col.sticky?'first':''} ${sort.key===col.key?'active':''}`}
                    style={{
                      ...(col.divEnd?{borderRight:'1px solid var(--border)'}:undefined),
                      ...(col.key==='_E'?{textAlign:'center'}:undefined),
                    }}
                    onClick={()=>onSort(col.key,col.numeric!==false)}>
                  {col.label}<span className="sort">{sort.key===col.key?(sort.dir==='desc'?'▼':'▲'):''}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(p=>(
              <tr key={p.playerName} onClick={()=>onOpenPlayer(p)}>
                {TEAM_COLS.map(col=>col.sticky?(
                  <td key={col.key} className="first" style={col.divEnd?{borderRight:'1px solid var(--border)'}:undefined}>
                    <div className="player-cell">
                      <div className="player-num">{p.number||'—'}</div>
                      <div style={{display:'flex',flexDirection:'column',gap:1,minWidth:0,lineHeight:1.2}}>
                        <span>{p.playerName}</span>
                        {p._pos&&<span style={{fontSize:10,fontWeight:600,color:'var(--text-3)',letterSpacing:'0.06em'}}>{p._pos}</span>}
                      </div>
                    </div>
                  </td>
                ):(
                  <td key={col.key} className={`${col.rate?'rate':''} ${col.hi?'hi':''} ${p[col.key]===0||p[col.key]==null?'zero':''}`}
                      style={{
                        ...(col.divEnd?{borderRight:'1px solid var(--border)'}:undefined),
                        ...(col.key==='_E'?{textAlign:'center'}:undefined),
                      }}>
                    {fmt.cell(p[col.key],col.fmt)}
                  </td>
                ))}
              </tr>
            ))}
            {sorted.length===0&&<tr><td colSpan={TEAM_COLS.length} style={{textAlign:'center',padding:36,color:'var(--text-3)'}}>No matching players.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
